import { useState } from "react";
import { ShieldCheck, FileBadge2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * DriverAttestationCard · v2.6.8 batch M
 * Shown under the green Verified badge — surfaces OSBI Fingerprint Background
 * Check and Oklahoma DMV Driving Record Verified attestations and a button
 * that opens the per-vehicle Taxi Cab License at ~50% viewport.
 *
 * Props:
 *   driver: {
 *     id, name, verified_osbi, verified_dmv,
 *     taxi_license_url, taxi_license_file_id,
 *   }
 *   variant: "self" (driver looking at their own profile) | "rider" (rider
 *            verifying their assigned driver) — controls the heading copy.
 */
export default function DriverAttestationCard({ driver, variant = "rider" }) {
  const [licOpen, setLicOpen] = useState(false);
  if (!driver) return null;
  const lic = driver.taxi_license_url
    || (driver.taxi_license_file_id ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/${driver.taxi_license_file_id}` : null);
  const osbi = !!driver.verified_osbi;
  const dmv = !!driver.verified_dmv;

  return (
    <div className="rounded-md border border-green-400/30 bg-green-400/5 p-3 space-y-2" data-testid="driver-attestation-card">
      <div className="text-[10px] uppercase tracking-wider text-green-300 font-bold">
        {variant === "self" ? "Your background verifications" : "Driver background verifications"}
      </div>
      <Attestation label="OSBI Background Check - Verified" verified={osbi}/>
      <Attestation label="OKDMV Driving Record - Verified" verified={dmv}/>
      <Button
        data-testid="view-taxi-license-btn"
        onClick={() => setLicOpen(true)}
        disabled={!lic}
        className="w-full h-9 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold disabled:bg-zinc-700 disabled:text-zinc-500"
      >
        <FileBadge2 className="h-4 w-4 mr-1"/>
        {lic ? "TAXIcab PERMIT" : "Taxi permit not on file"}
      </Button>

      <Dialog open={licOpen} onOpenChange={setLicOpen}>
        {/* v2.6.8 batch M · half-size modal per user request (~50% viewport on
            desktop; near-full on mobile via max-w breakpoints) */}
        <DialogContent className="bg-zinc-950 border-zinc-700 text-zinc-100 w-[95vw] sm:w-[50vw] sm:max-w-[50vw] max-h-[85vh] overflow-auto p-4" data-testid="taxi-license-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><FileBadge2 className="h-5 w-5 text-amber-400"/>Taxi Cab License · {driver.name}</span>
              <button onClick={() => setLicOpen(false)} className="text-zinc-400 hover:text-zinc-100" data-testid="taxi-license-close"><X className="h-5 w-5"/></button>
            </DialogTitle>
          </DialogHeader>
          {lic && (
            <div className="mt-2">
              <img
                src={lic}
                alt={`${driver.name} taxi cab license`}
                className="w-full h-auto rounded border border-zinc-800"
                data-testid="taxi-license-image"
              />
              <p className="text-[10px] text-zinc-500 mt-2 text-center">
                Occupational Tax License · taxi-permit-photo · {driver.vehicle_make ? `${driver.vehicle_make} ${driver.vehicle_model || ""}` : ""}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Attestation({ label, verified }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`attestation-${label.toLowerCase().includes("osbi") ? "osbi" : "dmv"}`}>
      <ShieldCheck className={`h-4 w-4 flex-shrink-0 ${verified ? "text-green-400" : "text-zinc-600"}`}/>
      <span className={verified ? "text-zinc-200" : "text-zinc-500 line-through"}>{label}</span>
      {verified && <span className="ml-auto text-[9px] uppercase tracking-wider text-green-400 font-bold">✓</span>}
    </div>
  );
}
